import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import {
  Archive, Check, ChevronLeft, ChevronRight, Download, ExternalLink,
  Film, Heart, ImageIcon, Layers, MessageCircle, Play, X,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { estErreurApi, fraicheurFr, heureFr, nombreFr } from './lib.js'
import { InsightsChart } from './InstagramChart.js'
import { mediaDownloadUrl, mediaFileUrl, type ContentArchive, type ContentReading } from './InstagramData.js'
import { SectionCommentaires } from './InstagramComments.js'

/**
 * MÉDIATHÈQUE INSTAGRAM (V1.2) — les VRAIS médias, dans leurs VRAIS formats.
 *
 * Tout ce qui s'affiche vient de l'ARCHIVE GESTION (assets locaux, servis
 * par l'API authentifiée) — jamais du CDN Meta. Une Story archivée reste
 * visible après son expiration chez Instagram ; une Story jamais capturée
 * l'affiche honnêtement, sans visuel inventé.
 *
 * Grille ratio-aware (colonnes CSS, aspect-ratio réservé depuis la base :
 * zéro layout shift), Stories/Reels en 9:16, posts dans leur format réel.
 * AUCUN drawer.
 */

const KIND_FR: Record<string, string> = {
  REEL: 'Reel', IMAGE: 'Publication', CAROUSEL_ALBUM: 'Carrousel', VIDEO: 'Vidéo', STORY: 'Story',
}

const METRIC_FR: Record<string, string> = {
  views: 'Vues', reach: 'Comptes atteints', likes: 'J’aime', comments: 'Commentaires',
  shares: 'Partages', saved: 'Sauvegardes', total_interactions: 'Interactions',
  replies: 'Réponses', navigation: 'Navigation', follows: 'Abonnements',
  profile_visits: 'Visites du profil',
  ig_reels_avg_watch_time: 'Temps de visionnage moyen',
  ig_reels_video_view_total_time: 'Temps de visionnage total',
}

function valeurAffichee(cle: string, v: number): string {
  if (cle === 'ig_reels_avg_watch_time') return `${(v / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s`
  if (cle === 'ig_reels_video_view_total_time') {
    const s = Math.round(v / 1000)
    return s >= 3600 ? `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min` : s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`
  }
  return nombreFr(v)
}

function dureeCourte(ms: number | null): string | null {
  if (!ms) return null
  const s = Math.round(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function ratioDe(archive: ContentArchive, kind: string | null): number {
  if (archive.width && archive.height) return archive.width / archive.height
  return kind === 'STORY' || kind === 'REEL' ? 9 / 16 : 1
}

/** Story encore dans sa fenêtre de vie chez Meta (24 h) ? */
function storyEnLigne(c: ContentReading): boolean {
  return c.kind === 'STORY' && c.published_at !== null
    && Date.now() - new Date(c.published_at).getTime() < 24 * 3_600_000
}

/* ─── Placeholder PREMIUM par type (§122) — jamais un pictogramme unique ─── */

function PlaceholderMedia({ kind }: { kind: string | null }) {
  const [Icone, texte] = kind === 'STORY' ? [Film, 'Story — média non archivé']
    : kind === 'REEL' ? [Play, 'Reel non archivé']
    : kind === 'CAROUSEL_ALBUM' ? [Layers, 'Carrousel non archivé']
    : [ImageIcon, 'Image indisponible']
  return (
    <div className={`mg-placeholder mg-placeholder--${(kind ?? 'image').toLowerCase()}`}>
      <Icone size={26} aria-hidden="true" />
      <span>{texte}</span>
    </div>
  )
}

/* ═══════════════════ LISTE — /communication/instagram/contenus ═════════ */

const FILTRES_TYPE = [
  { value: '', label: 'Tous' },
  { value: 'STORY', label: 'Stories' },
  { value: 'REEL', label: 'Reels' },
  { value: 'IMAGE', label: 'Posts' },
  { value: 'CAROUSEL_ALBUM', label: 'Carrousels' },
] as const

const FILTRES_PERIODE = [
  { value: '', label: 'Tout' },
  { value: '7', label: '7 j' },
  { value: '30', label: '30 j' },
  { value: '90', label: '90 j' },
  { value: '365', label: 'Année' },
] as const

const TRIS = [
  { value: 'recent', label: 'Plus récent' },
  { value: 'oldest', label: 'Plus ancien' },
  { value: 'views', label: 'Plus vu' },
  { value: 'reach', label: 'Plus de reach' },
  { value: 'interactions', label: 'Plus d’interactions' },
  { value: 'comments', label: 'Plus commentés' },
] as const

const FILTRES_ARCHIVE = [
  { value: 'all', label: 'Tout archivage' },
  { value: 'original', label: 'Original disponible' },
  { value: 'preview_only', label: 'Preview uniquement' },
  { value: 'none', label: 'Non archivé' },
] as const

function BadgeArchive({ c }: { c: ContentReading }) {
  if (storyEnLigne(c)) return <span className="lc-pill lc-pill--ok mg-badge"><span className="lc-dot lc-dot--ok lc-dot--live" />En ligne</span>
  if (c.archive.state === 'ORIGINAL') return <span className="lc-pill lc-pill--idle mg-badge"><Archive size={11} aria-hidden="true" /> Archivée</span>
  if (c.archive.state === 'NONE') return <span className="lc-pill lc-pill--warning mg-badge">Média non archivé</span>
  return null
}

function CarteMedia({ c, selectionne, modeSelection, onToggle }: {
  c: ContentReading
  selectionne: boolean
  modeSelection: boolean
  onToggle: () => void
}) {
  const ratio = ratioDe(c.archive, c.kind)
  const m = c.metrics
  const kpis: string[] = []
  if (typeof m['views'] === 'number') kpis.push(`${nombreFr(m['views'])} vues`)
  if (typeof m['reach'] === 'number') kpis.push(`${nombreFr(m['reach'])} atteints`)
  if (kpis.length < 2 && typeof m['total_interactions'] === 'number') kpis.push(`${nombreFr(m['total_interactions'])} interactions`)
  if (kpis.length === 0 && c.like_count !== null) kpis.push(`${nombreFr(c.like_count)} j’aime`)
  const nbComm = c.metrics['comments'] ?? c.comments_count

  const corps = (
    <article className={`mg-card lc-in ${selectionne ? 'mg-card--selected' : ''}`}>
      <div className="mg-card__media" style={{ aspectRatio: String(ratio) }}>
        {c.archive.preview_asset_id
          ? <img src={mediaFileUrl(c.archive.preview_asset_id)} alt="" loading="lazy" className="mg-card__img" />
          : <PlaceholderMedia kind={c.kind} />}
        {(c.kind === 'REEL' || c.archive.original_mime === 'video/mp4') && c.archive.preview_asset_id ? (
          <span className="mg-card__play" aria-hidden="true"><Play size={22} /></span>
        ) : null}
        {c.kind === 'CAROUSEL_ALBUM' && c.children.length > 0 ? (
          <span className="mg-card__count"><Layers size={12} aria-hidden="true" /> {c.children.length}</span>
        ) : null}
        {c.archive.duration_ms ? <span className="mg-card__duree">{dureeCourte(c.archive.duration_ms)}</span> : null}
        {modeSelection ? (
          <span className={`mg-card__check ${selectionne ? 'mg-card__check--on' : ''}`} aria-hidden="true">
            {selectionne ? <Check size={14} /> : null}
          </span>
        ) : null}
        <div className="mg-card__overlay">
          <span className="mg-card__type">
            {KIND_FR[c.kind ?? ''] ?? 'Contenu'}
            {c.published_at ? ` · ${new Date(c.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}` : ''}
          </span>
          {kpis.length > 0 ? (
            <span className="mg-card__kpis">
              {kpis.join(' · ')}
              {typeof nbComm === 'number' && nbComm > 0
                ? <span className="mg-card__comm"><MessageCircle size={12} aria-hidden="true" /> {nombreFr(nbComm)}</span>
                : null}
            </span>
          ) : null}
        </div>
      </div>
      <div className="mg-card__foot">
        <BadgeArchive c={c} />
        {c.caption && c.kind !== 'STORY'
          ? <p className="mg-card__caption">{c.caption.length > 70 ? `${c.caption.slice(0, 70)}…` : c.caption}</p> : null}
      </div>
    </article>
  )

  if (modeSelection) {
    return (
      <button type="button" className="mg-cardwrap" onClick={onToggle}
        aria-pressed={selectionne} aria-label={`Sélectionner ${KIND_FR[c.kind ?? ''] ?? 'contenu'}`}>
        {corps}
      </button>
    )
  }
  return <Link href={`/communication/instagram/contenus/${c.id}`} className="mg-cardwrap">{corps}</Link>
}

export function IgContentListPage({ kindLock, titre, sousTitre }: {
  /** V2.1 — pages-fonctions (Publications/Reels/Stories) : type VERROUILLÉ. */
  kindLock?: 'FEED' | 'REEL' | 'STORY'
  titre?: string
  sousTitre?: string
} = {}) {
  const meta = useShellMeta()
  const [type, setType] = useState<string>(kindLock ?? '')
  const [periode, setPeriode] = useState<string>('')
  const [tri, setTri] = useState<string>('recent')
  const [archive, setArchive] = useState<string>('all')
  const [items, setItems] = useState<ContentReading[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [erreur, setErreur] = useState<string | null>(null)
  const [selection, setSelection] = useState<Set<string>>(new Set())
  const [modeSelection, setModeSelection] = useState(false)

  useEffect(() => { setPage(0) }, [type, periode, tri, archive])
  useEffect(() => {
    const qp = new URLSearchParams()
    if (type) qp.set('kind', type)
    if (periode) qp.set('days', periode)
    if (tri !== 'recent') qp.set('sort', tri)
    if (archive !== 'all') qp.set('archive', archive)
    qp.set('page', String(page))
    void apiGet<{ items: ContentReading[]; total: number } | { error: string; message?: string }>(
      `/api/v1/communication/instagram/contenus?${qp}`)
      .then((j) => {
        if (estErreurApi(j)) { setErreur(j.message ?? 'Lecture impossible.'); return }
        setErreur(null); setTotal(j.total)
        setItems((prev) => (page === 0 ? j.items : [...(prev ?? []), ...j.items]))
      })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [type, periode, tri, archive, page])

  const portrait = type === 'STORY' || type === 'REEL'
  const toggle = (id: string) => {
    setSelection((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id); else if (n.size < 12) n.add(id)
      return n
    })
  }
  const exporter = () => {
    // GET natif : le navigateur streame le ZIP vers le disque.
    window.location.href = `/api/v1/communication/instagram/media/zip?ids=${[...selection].join(',')}`
  }

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">{titre ?? 'Médias'}</h1>
            <p className="lc-subtitle">{sousTitre ?? 'La médiathèque du club — médias archivés par Gestion, statistiques réelles.'}</p>
          </div>
          <div className="cm-head__actions">
            <button type="button" className={`lc-btn ${modeSelection ? 'lc-btn--primary' : ''}`}
              onClick={() => { setModeSelection((v) => !v); setSelection(new Set()) }}>
              {modeSelection ? 'Terminer la sélection' : 'Sélectionner'}
            </button>
          </div>
        </header>

        <div className="mg-filtres">
          {/* Type masqué quand la page-fonction le verrouille (§42-46). */}
          {kindLock ? null : (
          <div className="lc-seg" role="group" aria-label="Type de contenu">
            {FILTRES_TYPE.map((f) => (
              <button key={f.value} type="button"
                className={`lc-seg__btn ${type === f.value ? 'lc-seg__btn--on' : ''}`}
                aria-pressed={type === f.value} onClick={() => setType(f.value)}>{f.label}</button>
            ))}
          </div>
          )}
          <div className="lc-seg" role="group" aria-label="Période">
            {FILTRES_PERIODE.map((f) => (
              <button key={f.value} type="button"
                className={`lc-seg__btn ${periode === f.value ? 'lc-seg__btn--on' : ''}`}
                aria-pressed={periode === f.value} onClick={() => setPeriode(f.value)}>{f.label}</button>
            ))}
          </div>
          <select className="lc-input mg-select" value={tri} onChange={(e) => setTri(e.target.value)} aria-label="Tri">
            {TRIS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select className="lc-input mg-select" value={archive} onChange={(e) => setArchive(e.target.value)} aria-label="Archivage">
            {FILTRES_ARCHIVE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {erreur ? <p className="lc-hint">{erreur}</p> : null}
        {items === null && !erreur ? <p className="lc-hint">Chargement…</p> : null}
        {items && items.length === 0 ? <p className="lc-hint">Aucun contenu ne correspond à ces filtres.</p> : null}

        <div className={portrait ? 'mg-grid mg-grid--portrait' : 'mg-masonry'}>
          {(items ?? []).map((c) => (
            <CarteMedia key={c.id} c={c}
              selectionne={selection.has(c.id)} modeSelection={modeSelection}
              onToggle={() => toggle(c.id)} />
          ))}
        </div>

        {items && items.length < total ? (
          <button type="button" className="lc-btn mg-plus" onClick={() => setPage((p) => p + 1)}>
            Afficher plus ({items.length} / {total})
          </button>
        ) : null}

        {modeSelection && selection.size > 0 ? createPortal(
          <div className="lc-bulkbar-zone">
            <div className="lc-bulkbar" role="toolbar" aria-label="Export de la sélection">
              <span className="lc-bulkbar__count">
                <span className="lc-dot" /> {selection.size} contenu{selection.size > 1 ? 's' : ''} sélectionné{selection.size > 1 ? 's' : ''}
                {selection.size >= 12 ? ' (maximum)' : ''}
              </span>
              <span className="lc-bulkbar__actions">
                <button className="lc-btn lc-btn--primary" onClick={exporter}>
                  <Download size={14} aria-hidden="true" /> Télécharger (ZIP)
                </button>
              </span>
              <button className="lc-btn lc-btn--ghost lc-bulkbar__clear"
                onClick={() => setSelection(new Set())} aria-label="Vider la sélection">
                <X size={15} />
              </button>
            </div>
          </div>, document.body) : null}
      </main>
    </Shell>
  )
}

/* ═══════════════════ DÉTAIL — /communication/instagram/contenus/:id ════ */

interface ChildReading { provider_child_id: string; media_type: string; archive: ContentArchive }
interface Detail {
  content: ContentReading
  childrenAssets: ChildReading[]
  history: { metric_key: string; points: { day: string; value: number }[] }[]
}

/** Visionneuse d'UN élément : image contain, vidéo player natif (poster +
 *  Range pour le seek), plein écran natif. Story/Reel dans un cadre 9:16. */
function Visionneuse({ archive, kind }: { archive: ContentArchive; kind: string | null }) {
  const estVideo = archive.original_mime === 'video/mp4'
  const portrait = kind === 'STORY' || kind === 'REEL'
  if (archive.state === 'NONE' || (!archive.original_asset_id && !archive.preview_asset_id)) {
    return (
      <div className={`mg-viewer ${portrait ? 'mg-viewer--portrait' : ''}`}>
        <PlaceholderMedia kind={kind} />
        <p className="lc-hint mg-viewer__note">
          {kind === 'STORY'
            ? 'Les statistiques ont été conservées, mais le fichier n’était plus disponible lors de l’activation de l’archivage.'
            : 'Le fichier n’a pas pu être archivé — nouvel essai à la prochaine synchronisation si la source réapparaît.'}
        </p>
      </div>
    )
  }
  if (estVideo && archive.original_asset_id) {
    return (
      <div className={`mg-viewer ${portrait ? 'mg-viewer--portrait' : ''}`}>
        <video className="mg-viewer__video" controls playsInline preload="metadata"
          poster={archive.preview_asset_id ? mediaFileUrl(archive.preview_asset_id) : undefined}
          src={mediaFileUrl(archive.original_asset_id)}
          style={portrait ? { aspectRatio: '9 / 16' } : undefined} />
      </div>
    )
  }
  const src = archive.original_asset_id && !estVideo
    ? mediaFileUrl(archive.original_asset_id)
    : archive.preview_asset_id ? mediaFileUrl(archive.preview_asset_id) : null
  return (
    <div className={`mg-viewer ${portrait ? 'mg-viewer--portrait' : ''}`}>
      {src ? (
        <img src={src} alt="" className="mg-viewer__img"
          onClick={(e) => { void (e.currentTarget.requestFullscreen?.() ?? null) }}
          title="Cliquer pour le plein écran" />
      ) : <PlaceholderMedia kind={kind} />}
    </div>
  )
}

export function IgContentDetailPage({ id }: { id: string }) {
  const meta = useShellMeta()
  const [d, setD] = useState<Detail | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [slide, setSlide] = useState(0)
  useEffect(() => {
    void apiGet<Detail | { error: string; message?: string }>(`/api/v1/communication/instagram/contenus/${id}`)
      .then((j) => { if (estErreurApi(j)) setErreur('Contenu introuvable.'); else setD(j) })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [id])
  const c = d?.content
  const enfants = d?.childrenAssets ?? []
  const estCarrousel = (c?.children.length ?? 0) > 0
  const actif: ContentArchive | null = estCarrousel
    ? (enfants[Math.min(slide, enfants.length - 1)]?.archive ?? null)
    : (c?.archive ?? null)
  const zipDisponible = estCarrousel && enfants.some((e) => e.archive.original_asset_id)

  const metriquesTriees = useMemo(() =>
    Object.entries(c?.metrics ?? {}).sort((a, b) =>
      (a[0] === 'views' ? -1 : b[0] === 'views' ? 1 : 0)), [c])

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <SmartBack fallback="/communication/instagram/contenus" />
        {erreur ? <p className="lc-hint">{erreur}</p> : null}
        {c ? (
          <>
            <header className="cm-head">
              <div>
                <h1 className="lc-title">{KIND_FR[c.kind ?? ''] ?? 'Contenu'} Instagram</h1>
                <p className="lc-subtitle">
                  {heureFr(c.published_at)} <BadgeArchive c={c} />
                </p>
              </div>
              <div className="cm-head__actions">
                {actif?.original_asset_id ? (
                  <a href={mediaDownloadUrl(actif.original_asset_id)} className="lc-btn lc-btn--primary">
                    <Download size={14} aria-hidden="true" />
                    {estCarrousel ? 'Télécharger cet élément'
                      : actif.original_mime === 'video/mp4' ? 'Télécharger la vidéo' : 'Télécharger l’original'}
                  </a>
                ) : (
                  <button type="button" className="lc-btn" disabled title="Original non archivé.">
                    <Download size={14} aria-hidden="true" /> Télécharger l’original
                  </button>
                )}
                {zipDisponible ? (
                  <a href={`/api/v1/communication/instagram/contenus/${c.id}/zip`} className="lc-btn">
                    <Layers size={14} aria-hidden="true" /> Télécharger le carrousel
                  </a>
                ) : null}
                {c.permalink ? (
                  <a href={c.permalink} target="_blank" rel="noreferrer" className="lc-btn">
                    Instagram <ExternalLink size={14} aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            </header>

            <div className="mg-detail">
              <div className="mg-detail__media">
                {actif ? <Visionneuse archive={actif} kind={estCarrousel ? (enfants[slide]?.media_type === 'VIDEO' ? 'VIDEO' : 'IMAGE') : c.kind} /> : null}
                {estCarrousel ? (
                  <div className="mg-slider">
                    <button type="button" className="lc-btn mg-slider__nav" disabled={slide === 0}
                      onClick={() => setSlide((v) => Math.max(0, v - 1))} aria-label="Élément précédent">
                      <ChevronLeft size={16} />
                    </button>
                    <div className="mg-slider__thumbs" role="tablist" aria-label="Éléments du carrousel">
                      {enfants.map((e, i) => (
                        <button key={e.provider_child_id} type="button" role="tab"
                          aria-selected={slide === i}
                          className={`mg-slider__thumb ${slide === i ? 'mg-slider__thumb--on' : ''}`}
                          onClick={() => setSlide(i)}>
                          {e.archive.preview_asset_id
                            ? <img src={mediaFileUrl(e.archive.preview_asset_id)} alt={`Élément ${i + 1}`} />
                            : <span className="mg-slider__ph">{i + 1}</span>}
                        </button>
                      ))}
                    </div>
                    <button type="button" className="lc-btn mg-slider__nav" disabled={slide >= enfants.length - 1}
                      onClick={() => setSlide((v) => Math.min(enfants.length - 1, v + 1))} aria-label="Élément suivant">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                ) : null}
                <p className="lc-hint mg-source">
                  {actif?.captured_at
                    ? <>Source média : archivé par Gestion · capturé {heureFr(actif.captured_at)}{actif.original_bytes ? ` · ${(actif.original_bytes / 1_048_576).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo` : ''}</>
                    : 'Source média : non archivé'}
                  {c.metrics_captured_at ? <> · Source analytics : Instagram Insights ({fraicheurFr(c.metrics_captured_at)})</> : null}
                </p>
              </div>

              <div className="mg-detail__side">
                {c.caption ? <p className="cm-reviewfull mg-caption">{c.caption}</p> : null}
                <section className="lc-panel">
                  <header className="lc-panel__head"><h2 className="lc-section-title">Performance</h2>
                    {estCarrousel ? <span className="lc-hint">statistiques du carrousel entier</span> : null}
                  </header>
                  <div className="lc-panel__body">
                    {metriquesTriees.length === 0 ? (
                      <p className="lc-hint">Aucune métrique relevée pour ce contenu.</p>
                    ) : (
                      <div className="ig-kpis lc-stagger">
                        {metriquesTriees.map(([cle, v]) => (
                          <div key={cle} className="ig-kpi lc-in">
                            <strong>{valeurAffichee(cle, v)}</strong>
                            <span>{METRIC_FR[cle] ?? cle}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="cm-splitline" style={{ marginTop: 10 }}>
                      {c.like_count !== null ? <span><Heart size={13} aria-hidden="true" /> {nombreFr(c.like_count)}</span> : null}
                      {c.comments_count !== null ? <span><MessageCircle size={13} aria-hidden="true" /> {nombreFr(c.comments_count)}</span> : null}
                    </div>
                  </div>
                </section>

                <SectionCommentaires contentId={c.id} />

                {(d?.history ?? []).filter((h) => h.points.length >= 2).length > 0 ? (
                  <section className="lc-panel">
                    <header className="lc-panel__head"><h2 className="lc-section-title">Trajectoire</h2>
                      <span className="lc-hint">un point par jour capturé</span></header>
                    <div className="lc-panel__body ig-trajectoires">
                      {d!.history.filter((h) => h.points.length >= 2).slice(0, 4).map((h) => (
                        <div key={h.metric_key} className="ig-trajectoire">
                          <h3 className="cm-widget__sub">{METRIC_FR[h.metric_key] ?? h.metric_key}</h3>
                          <InsightsChart points={h.points} height={100} accent="var(--cyan)" />
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            </div>
          </>
        ) : !erreur ? <p className="lc-hint">Chargement…</p> : null}
      </main>
    </Shell>
  )
}
